import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import formatEnumLabel from './formatEnumLabel';
import { StaticAdElement } from './types';

interface StaticElementsTableProps {
  elements: StaticAdElement[];
}

/**
 * The extracted elements, in the order a viewer's eye takes them.
 *
 * The two flags get their own colour because they are the ones a later
 * regeneration acts on: `isProduct` marks what gets replaced, `isBranded` marks
 * what must be removed rather than carried into our own creative.
 */
export default function StaticElementsTable({ elements }: StaticElementsTableProps) {
  if (!elements.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
        No elements extracted yet.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 40 }}>#</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Text / description</TableCell>
          <TableCell sx={{ width: 120 }}>Position</TableCell>
          <TableCell sx={{ width: 110 }}>Weight</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {elements.map(el => (
          <TableRow key={el.elementId} hover>
            <TableCell>{el.readingOrder}</TableCell>
            <TableCell>
              <Stack spacing={0.5} alignItems="flex-start">
                <Typography variant="body2">{formatEnumLabel(el.elementRole) || '—'}</Typography>
                {el.isProduct && <Chip size="small" color="primary" label="Product" sx={{ height: 18, fontSize: '0.6rem' }} />}
                {el.isBranded && <Chip size="small" color="warning" label="Branded" sx={{ height: 18, fontSize: '0.6rem' }} />}
              </Stack>
            </TableCell>
            <TableCell>
              {el.elementText && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                    {el.elementText}
                  </Typography>
                  <Tooltip title="Copy this text exactly">
                    <IconButton
                      size="small"
                      onClick={() => navigator.clipboard?.writeText(el.elementText || '')}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              {el.description && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {el.description}
                </Typography>
              )}
              {el.whyItWorks && (
                <Typography variant="caption" color="text.disabled" display="block" sx={{ fontStyle: 'italic' }}>
                  {el.whyItWorks}
                </Typography>
              )}
            </TableCell>
            <TableCell>{formatEnumLabel(el.gridPosition) || '—'}</TableCell>
            <TableCell>{formatEnumLabel(el.prominence) || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
